<template>
  <div>


    <!-- Company Story -->
    <section class="relative py-20 overflow-hidden scroll-animate">
      <!-- Magical Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <!-- Floating Orbs -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-bounce"></div>
        <div class="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-bounce" style="animation-delay: 1s;"></div>
      </div>

      <div class="container-custom relative z-10">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-6 text-gray-900">
            {{ t('about.story.title') }}
          </h2>
        </div>

        <div class="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-12 items-center">
          <div>
            <p class="text-lg text-gray-600 mb-6 leading-relaxed">
              {{ t('about.story.paragraph1') }}
            </p>
            <p class="text-lg text-gray-600 mb-6 leading-relaxed">
              {{ t('about.story.paragraph2') }}
            </p>
            <p class="text-lg text-gray-600 leading-relaxed">
              {{ t('about.story.paragraph3') }}
            </p>
          </div>
          <div class="relative">
            <div class="magical-story-card group">
              <div class="story-glow"></div>
              <div class="story-card">
                <div class="text-center">
                  <div class="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg
                      class="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h3 class="text-2xl font-bold text-gray-900 mb-4">
                    {{ t('about.story.experienceTitle') }}
                  </h3>
                  <p class="text-gray-600">
                    {{ t('about.story.experienceSubtitle') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Trusted Brands Section -->
    <section class="relative py-20 overflow-hidden scroll-animate">
      <!-- Magical Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <!-- Floating Orbs -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-bounce"></div>
        <div class="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-bounce" style="animation-delay: 1s;"></div>
      </div>

      <div class="container-custom relative z-10">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-6 text-gray-900">
            {{ t('home.brands.title') }}
          </h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">
            {{ t('home.brands.subtitle') }}
          </p>
        </div>

        <!-- Brands Grid -->
        <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
          <div class="brand-logo-container">
            <img
              src="/images/Brands/IMG-20250911-WA0002.jpg"
              alt="Brand Logo 2"
              class="brand-logo"
              loading="lazy"
            />
          </div>
          <div class="brand-logo-container">
            <img
              src="\images\Brands\IMG-20250911-WA0003.jpg"
              alt="Brand Logo 3"
              class="brand-logo"
              loading="lazy"
            />
          </div>
          <div class="brand-logo-container">
            <img
              src="/images/Brands/IMG-20250911-WA0004.jpg"
              alt="Brand Logo 4"
              class="brand-logo"
              loading="lazy"
            />
          </div>
          <div class="brand-logo-container">
            <img
              src="/images/Brands/IMG-20250911-WA0005.jpg"
              alt="Brand Logo 5"
              class="brand-logo"
              loading="lazy"
            />
          </div>
          <div class="brand-logo-container">
            <img
              src="/images/Brands/IMG-20250911-WA0006.jpg"
              alt="Brand Logo 6"
              class="brand-logo"
              loading="lazy"
            />
          </div>
          <div class="brand-logo-container">
            <img
              src="/images/Brands/IMG-20250911-WA0007.jpg"
              alt="Brand Logo 7"
              class="brand-logo"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Mission & Vision -->
    <section class="relative py-20 overflow-hidden scroll-animate">
      <!-- Magical Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <!-- Floating Orbs -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-bounce"></div>
        <div class="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-bounce" style="animation-delay: 1s;"></div>
      </div>

      <div class="container-custom relative z-10">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-6 text-gray-900">
            {{ t('about.mission.sectionTitle') }}
          </h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Mission -->
          <div class="magical-mission-card group">
            <div class="mission-glow"></div>
            <div class="mission-card">
              <div class="text-center">
                <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg
                    class="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ t('about.mission.title') }}</h3>
                <p class="text-gray-600 leading-relaxed">
                  {{ t('about.mission.description') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Vision -->
          <div class="magical-vision-card group">
            <div class="vision-glow"></div>
            <div class="vision-card">
              <div class="text-center">
                <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg
                    class="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ t('about.vision.title') }}</h3>
                <p class="text-gray-600 leading-relaxed">
                  {{ t('about.vision.description') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Values -->
    <section class="relative py-20 overflow-hidden scroll-animate">
      <!-- Magical Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <!-- Floating Orbs -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-bounce"></div>
        <div class="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-bounce" style="animation-delay: 1s;"></div>
      </div>

      <div class="container-custom relative z-10">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-6 text-gray-900">
            {{ t('about.values.title') }}
          </h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">
            {{ t('about.values.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <!-- Quality -->
          <div class="magical-values-card group">
            <div class="values-glow"></div>
            <div class="values-card text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ t('about.values.quality.title') }}
              </h3>
              <p class="text-gray-600">
                {{ t('about.values.quality.description') }}
              </p>
            </div>
          </div>

          <!-- Customer Focus -->
          <div class="magical-values-card group">
            <div class="values-glow"></div>
            <div class="values-card text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ t('about.values.customer.title') }}
              </h3>
              <p class="text-gray-600">
                {{ t('about.values.customer.description') }}
              </p>
            </div>
          </div>

          <!-- Technical Excellence -->
          <div class="magical-values-card group">
            <div class="values-glow"></div>
            <div class="values-card text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ t('about.values.technical.title') }}
              </h3>
              <p class="text-gray-600">
                {{ t('about.values.technical.description') }}
              </p>
            </div>
          </div>

          <!-- Innovation -->
          <div class="magical-values-card group">
            <div class="values-glow"></div>
            <div class="values-card text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ t('about.values.innovation.title') }}
              </h3>
              <p class="text-gray-600">
                {{ t('about.values.innovation.description') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Overview -->
    <section class="relative py-20 overflow-hidden scroll-animate">
      <!-- Magical Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <!-- Floating Orbs -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-bounce"></div>
        <div class="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-bounce" style="animation-delay: 1s;"></div>
      </div>

      <div class="container-custom relative z-10">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-6 text-gray-900">
            {{ t('about.services.title') }}
          </h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">
            {{ t('about.services.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <!-- Machine Sales -->
          <div class="magical-services-card group">
            <div class="services-glow"></div>
            <div class="services-card text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-4">
                {{ t('about.services.machines.title') }}
              </h3>
              <p class="text-gray-600 mb-6">
                {{ t('about.services.machines.description') }}
              </p>
              <ul class="text-sm text-gray-600 space-y-2 text-left">
                <li class="flex items-center"><span class="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>{{ t('about.services.machines.features.0') }}</li>
                <li class="flex items-center"><span class="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>{{ t('about.services.machines.features.1') }}</li>
                <li class="flex items-center"><span class="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>{{ t('about.services.machines.features.2') }}</li>
                <li class="flex items-center"><span class="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>{{ t('about.services.machines.features.3') }}</li>
              </ul>
            </div>
          </div>

          <!-- Spare Parts -->
          <div class="magical-services-card group">
            <div class="services-glow"></div>
            <div class="services-card text-center">
              <div class="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-4">
                {{ t('about.services.spareParts.title') }}
              </h3>
              <p class="text-gray-600 mb-6">
                {{ t('about.services.spareParts.description') }}
              </p>
              <ul class="text-sm text-gray-600 space-y-2 text-left">
                <li class="flex items-center"><span class="w-2 h-2 bg-green-600 rounded-full mr-3"></span>{{ t('about.services.spareParts.features.0') }}</li>
                <li class="flex items-center"><span class="w-2 h-2 bg-green-600 rounded-full mr-3"></span>{{ t('about.services.spareParts.features.1') }}</li>
                <li class="flex items-center"><span class="w-2 h-2 bg-green-600 rounded-full mr-3"></span>{{ t('about.services.spareParts.features.2') }}</li>
                <li class="flex items-center"><span class="w-2 h-2 bg-green-600 rounded-full mr-3"></span>{{ t('about.services.spareParts.features.3') }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="relative py-20 overflow-hidden scroll-animate">
      <!-- Magical Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <!-- Floating Orbs -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-bounce"></div>
        <div class="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-bounce" style="animation-delay: 1s;"></div>
      </div>

      <div class="container-custom relative z-10">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-6 text-gray-900">
            {{ t('about.whyChoose.title') }}
          </h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">
            {{ t('about.whyChoose.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div class="space-y-6">
            <div class="magical-why-card group">
              <div class="why-glow"></div>
              <div class="why-card">
                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                      {{ t('about.whyChoose.globalNetwork.title') }}
                    </h3>
                    <p class="text-gray-600">
                      {{ t('about.whyChoose.globalNetwork.description') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="magical-why-card group">
              <div class="why-glow"></div>
              <div class="why-card">
                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                      {{ t('about.whyChoose.expertTeam.title') }}
                    </h3>
                    <p class="text-gray-600">
                      {{ t('about.whyChoose.expertTeam.description') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="magical-why-card group">
              <div class="why-glow"></div>
              <div class="why-card">
                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                      {{ t('about.whyChoose.qualityAssurance.title') }}
                    </h3>
                    <p class="text-gray-600">
                      {{ t('about.whyChoose.qualityAssurance.description') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-6">
            <div class="magical-why-card group">
              <div class="why-glow"></div>
              <div class="why-card">
                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                      {{ t('about.whyChoose.reliableSupport.title') }}
                    </h3>
                    <p class="text-gray-600">
                      {{ t('about.whyChoose.reliableSupport.description') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="magical-why-card group">
              <div class="why-glow"></div>
              <div class="why-card">
                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                      {{ t('about.whyChoose.customSolutions.title') }}
                    </h3>
                    <p class="text-gray-600">
                      {{ t('about.whyChoose.customSolutions.description') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="magical-why-card group">
              <div class="why-glow"></div>
              <div class="why-card">
                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">
                      {{ t('about.whyChoose.longTermPartnership.title') }}
                    </h3>
                    <p class="text-gray-600">
                      {{ t('about.whyChoose.longTermPartnership.description') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="relative py-20 overflow-hidden scroll-animate">
      <!-- Magical Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <!-- Floating Orbs -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-bounce"></div>
        <div class="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-bounce" style="animation-delay: 1s;"></div>
      </div>

      <div class="container-custom relative z-10">
        <div class="text-center">
          <h2 class="text-4xl font-bold mb-6 text-gray-900">
            {{ t('about.cta.title') }}
          </h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            {{ t('about.cta.subtitle') }}
          </p>

          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <NuxtLink
              to="/machines"
              class="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              {{ t('about.cta.browseInventory') }}
            </NuxtLink>
            <NuxtLink
              to="/contact"
              class="inline-flex items-center px-6 py-3 bg-white hover:bg-gray-50 text-blue-600 font-semibold rounded-lg border border-blue-600 transition-colors duration-200"
            >
              {{ t('about.cta.getInTouch') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
const { t } = useI18n();

useHead({
  title: computed(() => t('meta.aboutTitle')),
  meta: [
    {
      name: "description",
      content: computed(() => t('meta.aboutDescription')),
    },
  ],
});

onMounted(() => {
  if (process.client) {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => {
      observer.observe(el);
    });
  }
});
</script>

<style scoped>
/* Magical Story Card */
.magical-story-card {
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.story-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.3), rgba(147, 51, 234, 0.3), rgba(236, 72, 153, 0.3));
  border-radius: 1rem;
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.story-card {
  position: relative;
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.magical-story-card:hover .story-glow {
  opacity: 1;
}

.magical-story-card:hover {
  transform: translateY(-4px);
}

/* Magical Mission Card */
.magical-mission-card {
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.mission-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.3), rgba(147, 51, 234, 0.3));
  border-radius: 1rem;
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.mission-card {
  position: relative;
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.magical-mission-card:hover .mission-glow {
  opacity: 1;
}

.magical-mission-card:hover {
  transform: translateY(-4px);
}

/* Magical Vision Card */
.magical-vision-card {
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.vision-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, rgba(147, 51, 234, 0.3), rgba(236, 72, 153, 0.3));
  border-radius: 1rem;
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.vision-card {
  position: relative;
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.magical-vision-card:hover .vision-glow {
  opacity: 1;
}

.magical-vision-card:hover {
  transform: translateY(-4px);
}

/* Magical Values Cards */
.magical-values-card {
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.values-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.3), rgba(34, 197, 94, 0.3), rgba(147, 51, 234, 0.3), rgba(249, 115, 22, 0.3));
  border-radius: 1rem;
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.values-card {
  position: relative;
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.magical-values-card:hover .values-glow {
  opacity: 1;
}

.magical-values-card:hover {
  transform: translateY(-4px);
}

/* Magical Services Cards */
.magical-services-card {
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.services-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.3), rgba(34, 197, 94, 0.3));
  border-radius: 1rem;
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.services-card {
  position: relative;
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.magical-services-card:hover .services-glow {
  opacity: 1;
}

.magical-services-card:hover {
  transform: translateY(-4px);
}

/* Magical Why Choose Cards */
.magical-why-card {
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.why-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.3), rgba(147, 51, 234, 0.3));
  border-radius: 1rem;
  filter: blur(8px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.why-card {
  position: relative;
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.magical-why-card:hover .why-glow {
  opacity: 1;
}

.magical-why-card:hover {
  transform: translateY(-4px);
}

/* Scroll Animations */
.scroll-animate {
  opacity: 1;
  transform: translateY(0);
  transition: all 0.6s ease-out;
}

.scroll-animate:not(.animate-in) {
  opacity: 0;
  transform: translateY(30px);
}

.scroll-animate.animate-in {
  opacity: 1;
  transform: translateY(0);
}

/* Brand Logo Styling */
.brand-logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  padding: 1rem;
  transition: transform 0.3s ease, filter 0.3s ease;
}

.brand-logo-container:hover {
  transform: scale(1.05);
  filter: brightness(1.1);
}

.brand-logo {
  max-width: 100%;
  max-height: 60px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: grayscale(100%);
  transition: filter 0.3s ease;
}

.brand-logo-container:hover .brand-logo {
  filter: grayscale(0%);
}
</style>
